import { isNil } from 'lodash';
import { CSSProperties, ReactNode, useCallback, useContext, useState } from 'react';
import { AnchoredSheet } from '@shared/components/AnchoredSheet';
import { View } from '@shared/components/View';
import { GeolocationMapPreview } from '@shared/components/blocks/lib/inputGeolocation/mapPreview';
import { MBIconV2 } from '@shared/components/image';
import { MBListRow } from '@shared/components/list';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBSearchBar } from '@shared/components/searchBar';
import { MBTextBody1 } from '@shared/components/typography';
import { AddressField, AddressFieldValue } from '@shared/data/entity/fields/AddressField';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { GooglePlacesAutocompleteSuggestion } from '@shared/util/googleMaps/types';
import {
  useFetchGooglePlacesCoords,
  useGooglePlacesAutocomplete,
} from '@shared/util/googleMaps/useGooglePlacesAutocomplete';
import { useBoolean } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { useResponsiveFormContext } from '../ResponsiveFormContext';
import { EditFieldContainer } from './EditFieldContainer';

interface Props {
  field: AddressField | null;
  component: API.AddressComponentFragment | API.RelatedCardLookupComponent;
  updateField?: (componentId: string, field: AddressField | null) => void;
  error?: string;
  showIcon?: boolean;
  label?: string;
  showLabel?: boolean;
  indentLabel?: boolean;
  reserveIconSpace?: boolean;
  compactTop?: boolean;
  compactBottom?: boolean;
  subfooter?: ReactNode;
  isGrayScale?: boolean;
  isItalic?: boolean;
  style?: CSSProperties;
}

export function AddressEditField({
  field,
  component,
  updateField,
  label,
  error,
  showIcon,
  showLabel,
  indentLabel,
  reserveIconSpace,
  compactTop,
  compactBottom,
  subfooter,
  isGrayScale,
  isItalic,
  style,
}: Props) {
  const responsiveForms = useResponsiveFormContext();
  const anchorName = `--address-edit-${component.id}`;
  const colors = useContext(ColorThemeContext);

  const [isModalOpen, openModal, closeModal] = useBoolean();
  const [searchQuery, setSearchQuery] = useState('');
  const { suggestions, loading } = useGooglePlacesAutocomplete(searchQuery);
  const fetchGooglePlacesCoords = useFetchGooglePlacesCoords();

  const onChooseSuggestion = useCallback(
    async (suggestion: GooglePlacesAutocompleteSuggestion) => {
      closeModal();
      setSearchQuery('');
      const coords = await fetchGooglePlacesCoords(suggestion.googlePlaceId);
      const updatedAddress: AddressFieldValue = { ...suggestion, coords };
      updateField?.(component.id, new AddressField(updatedAddress));
    },
    [component.id, fetchGooglePlacesCoords, closeModal, updateField],
  );

  const onClear = useCallback(() => updateField?.(component.id, null), [component.id, updateField]);

  const address = field?.address;
  const hasAddress = !isNil(address);
  const isReadOnly = !!component.readonly || isNil(updateField);
  const showClearButton = responsiveForms && hasAddress && !isReadOnly;

  return (
    <EditFieldContainer
      onPress={isReadOnly ? undefined : openModal}
      component={component}
      anchorName={anchorName}
      showIcon={showIcon ?? (responsiveForms ? !hasAddress : true)}
      label={label}
      showLabel={!isNil(showLabel) ? showLabel : responsiveForms || hasAddress}
      indentLabel={indentLabel}
      reserveIconSpace={reserveIconSpace}
      showPlaceholder={!hasAddress}
      showClearButton={showClearButton}
      onClear={onClear}
      error={error}
      compactTop={compactTop}
      compactBottom={compactBottom}
      largerInput={!hasAddress}
      subfooter={subfooter}
      isGrayScale={isGrayScale}
      isItalic={isItalic}
      style={style}
    >
      {hasAddress && !responsiveForms && (
        <AddressPreview address={address} isReadOnly={isReadOnly} onClear={onClear} />
      )}
      {hasAddress && responsiveForms && (
        <SquareAddressPreview address={address} isReadOnly={isReadOnly} />
      )}
      {isModalOpen && (
        <AnchoredSheet anchorName={anchorName} initialSize="full" onClose={closeModal}>
          <View padding={1}>
            <MBSearchBar
              value={searchQuery}
              onChangeText={setSearchQuery}
              autoFocus
              placeholder={i18n(`card.blocks.addressInput.searchPlaceholder`)}
            />
          </View>

          {suggestions.map(suggestion => (
            <MBListRow
              image={<MBIconV2 color={colors.foregroundInactive} name="pin-location" />}
              paddingLeft={1}
              key={suggestion.googlePlaceId}
              title={suggestion.title}
              subtitle={suggestion.subtitle}
              isHidingPressableIndicator
              onPress={() => onChooseSuggestion(suggestion)}
            />
          ))}
          {loading && <MBLoadingIndicator padding={2} />}
        </AnchoredSheet>
      )}
    </EditFieldContainer>
  );
}

export function AddressPreview({
  address,
  isReadOnly,
  onClear,
}: {
  address: AddressFieldValue;
  isReadOnly: boolean;
  onClear: VoidFunction;
}) {
  const colors = useContext(ColorThemeContext);
  return (
    <View paddingBottom={0.5}>
      <View paddingBottom={1}>
        <MBTextBody1 color={colors.foregroundActive}>{address.title}</MBTextBody1>
        <MBTextBody1 color={colors.foregroundInactive}>{address.subtitle}</MBTextBody1>
      </View>
      <MapPreview address={address} isReadOnly={isReadOnly} onClear={onClear} />
    </View>
  );
}

export function SquareAddressPreview({
  address,
  isReadOnly,
  onClear,
}: {
  address: AddressFieldValue;
  isReadOnly: boolean;
  onClear?: VoidFunction;
}) {
  const colors = useContext(ColorThemeContext);
  return (
    <View row>
      <View>
        <MapPreview address={address} isReadOnly={isReadOnly} onClear={onClear} />
      </View>
      <View paddingHorizontal={2} paddingVertical={1.5}>
        <MBTextBody1 color={colors.foregroundActive}>{address.title}</MBTextBody1>
        <MBTextBody1 color={colors.foregroundInactive}>{address.subtitle}</MBTextBody1>
      </View>
    </View>
  );
}

function MapPreview({
  address,
  isReadOnly,
  onClear,
}: {
  address: AddressFieldValue;
  isReadOnly: boolean;
  onClear?: VoidFunction;
}) {
  const responsiveForms = useResponsiveFormContext();
  return (
    <GeolocationMapPreview
      width={responsiveForms ? grid(12) : undefined}
      height={responsiveForms ? grid(12) : undefined}
      lat={address.coords.lat ?? 0}
      lon={address.coords.lon ?? 0}
      iconUrl="https://assets.coastapp.com/address-pin.png"
      onClear={isReadOnly ? undefined : onClear}
    />
  );
}
